// Catalog.ID Client — The `cid/registry-authority` document
// ============================================================================
// The account's Registry AUTHORITY key stopped being a secret and became a
// pointer (cid-encrypted-storage §5.2, M5). Where the bag used to carry
// `registry_authority@n` — one random hybrid seed per generation, one
// supersecret prompt per mint, one forced re-unlock on every OTHER device of
// the account — the account now carries only this document: which generations
// exist, which one is current, and the fingerprint each one must produce.
//
// The key material itself is a pure function of the account's derive root:
//
//     seed64 = deriveModuleKey(root, "cid", "registry-authority:v1:<index>", 64)
//
// laid out exactly as `signingKeygen` produces and `signHybrid` /
// `signingPublicKeyFromSeed` consume — ed25519_seed(32) ‖ mldsa65_seed(32).
// Nothing stores that seed, anywhere, ever: it is derived inside the handler
// that needs it and zeroed in a `finally` (§4.1's invariant).
//
// ── why ONE INFO STRING PER GENERATION ─────────────────────────────────────
// HKDF is prefix-consistent (`crypto/derive.ts`), so two widths under one info
// string are the same key truncated. Generations therefore get their own info
// strings rather than their own widths or offsets: `…:v1:0` and `…:v1:1` are
// unrelated keys, which is the whole point of a rotation.
//
// ── why the fingerprint is STORED when it can be re-derived ────────────────
// It is the cross-check, not the source. A device that derives a different
// public key from the one the document records is a device whose root is wrong
// — a restored bundle from another account, a half-finished root rotation — and
// the ceremony handlers refuse to sign rather than produce a perfectly valid
// signature by an authority nobody expects (`authority-state-corrupt`).
//
// ── why retired generations stay ───────────────────────────────────────────
// After a rotation the OLD key is still the effective owner of every key-owned
// asset, so the mixed-estate ceremony needs it to sign the rebinds, and an
// `authority_rotate` chain needs it as the outer signer while the INCOMING key
// signs the proof (asset-ownership D9). An index is therefore never reused and
// a generation is never dropped: `retired` is a status, not a deletion.
//
// DOM-free, side-effect-free and network-free: the read/write half lives in
// `key-grant/ceremony-handlers.ts` over `state/cid-blobs.ts`.
// ============================================================================

import { encodeUtf8 } from './aes';
import { deriveModuleKey } from './derive';

/** The blob's name inside the vault's reserved `cid` namespace. LOAD-BEARING:
 *  `deriveBlobKey(root, 'cid', name)` mixes the name into the HKDF info, so
 *  changing it makes every stored document unopenable. */
export const REGISTRY_AUTHORITY_BLOB = 'registry-authority';

/** The only document version that exists. Pre-live: a document that is not v1
 *  is refused rather than upgraded. */
export const REGISTRY_AUTHORITY_DOCUMENT_VERSION = 1;

/**
 * Info-string prefix for a generation's seed derivation (§5.2). LOAD-BEARING
 * in the same way the blob name is: the full info is
 * `registry-authority:v1:<index>`, and every byte of it feeds the KDF, so a
 * change here re-keys every generation of every account.
 */
export const REGISTRY_AUTHORITY_INFO_PREFIX = 'registry-authority:v1:';

/**
 * The reserved namespace the seed derives under. The literal rather than an
 * import from `state/cid-blobs.ts` (`CID_BLOB_MODULE_ID`), so this module stays
 * free of the state layer — `crypto/*` is the pure half.
 */
const CID_MODULE_ID = 'cid';

/** Width of a hybrid Ed25519+ML-DSA-65 secret seed pair. Mirrors
 *  `signing.HYBRID_SECRET_SEED_LEN`, repeated rather than imported so this
 *  module pulls in no @noble keygen. */
const AUTHORITY_SEED_BYTES = 64;

/** 128 lowercase hex — SHA-512 of the 1984-byte combined public key. */
const FINGERPRINT_PATTERN = /^[0-9a-f]{128}$/;

/**
 * A generation's lifecycle. `retired` keeps the generation derivable and
 * signable; it means "no longer the one a NEW ceremony pins", never "gone".
 */
export type AuthorityGenerationStatus = 'active' | 'retired';

const STATUSES: readonly AuthorityGenerationStatus[] = ['active', 'retired'];

/** One authority generation. Field names and types ARE the stored contract:
 *  the document is serialized as RFC 8785 canonical JSON before encryption. */
export interface AuthorityGeneration {
    /** 0 for the genesis authority; each rotation appends the next. Never
     *  reused, so it is also the generation's KDF info. */
    index: number;
    /** 128-hex SHA-512 of the derived combined public key — the cross-check. */
    fingerprint: string;
    /** ISO-8601, for the ceremony console's display and for forensics. */
    createdAt: string;
    status: AuthorityGenerationStatus;
}

export interface RegistryAuthorityState {
    v: typeof REGISTRY_AUTHORITY_DOCUMENT_VERSION;
    /** Ascending by index, at most one `active`. */
    generations: AuthorityGeneration[];
}

/** The document an account with no authority key yet would hold. Never
 *  written as such — the first write appends generation 0 at the same time. */
export function emptyAuthorityState(): RegistryAuthorityState {
    return { v: REGISTRY_AUTHORITY_DOCUMENT_VERSION, generations: [] };
}

/** The KDF info bytes for one generation: `registry-authority:v1:<index>`. */
export function authorityInfoBytes(index: number): Uint8Array {
    requireIndex(index);
    return encodeUtf8(REGISTRY_AUTHORITY_INFO_PREFIX + index);
}

/**
 * This generation's 64-byte hybrid signing seed.
 *
 * The caller OWNS the returned bytes and must zero them when done (§4.1): the
 * whole point of deriving is that nothing keeps a copy.
 */
export function deriveAuthoritySeed(root: Uint8Array, index: number): Uint8Array {
    return deriveModuleKey(root, CID_MODULE_ID, authorityInfoBytes(index), AUTHORITY_SEED_BYTES);
}

/**
 * Validate and normalise a document read out of the blob store.
 *
 * Every field is re-checked: this is the only gate between bytes any node in
 * the federation may have replicated and a key that will sign ownership
 * ceremonies. Unknown fields are DROPPED rather than carried through, so a
 * read-modify-write never re-persists something this build does not understand.
 *
 * @throws Error naming the defect. Every caller turns that into a refusal —
 *         never into "assume there are no generations", which would mint a
 *         second generation 0 over the top of a document we failed to read.
 */
export function decodeAuthorityState(value: unknown): RegistryAuthorityState {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error('registry-authority: document is not an object');
    }
    const doc = value as Record<string, unknown>;
    if (doc['v'] !== REGISTRY_AUTHORITY_DOCUMENT_VERSION) {
        throw new Error(
            `registry-authority: unsupported document version ${String(doc['v'])} `
            + `(expected ${REGISTRY_AUTHORITY_DOCUMENT_VERSION})`);
    }
    const raw = doc['generations'];
    if (!Array.isArray(raw)) {
        throw new Error('registry-authority: generations must be an array');
    }

    const seen = new Set<number>();
    const generations: AuthorityGeneration[] = [];
    let actives = 0;
    for (let i = 0; i < raw.length; i++) {
        const generation = decodeGeneration(raw[i], i);
        if (seen.has(generation.index)) {
            throw new Error(
                `registry-authority: duplicate generation index ${generation.index}`);
        }
        seen.add(generation.index);
        if (generation.status === 'active') actives++;
        generations.push(generation);
    }
    if (actives > 1) {
        throw new Error(
            `registry-authority: ${actives} generations are active (at most one may be)`);
    }

    generations.sort((a, b) => a.index - b.index);
    return { v: REGISTRY_AUTHORITY_DOCUMENT_VERSION, generations };
}

function decodeGeneration(value: unknown, at: number): AuthorityGeneration {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`registry-authority: generation ${at} is not an object`);
    }
    const row = value as Record<string, unknown>;

    const index = row['index'];
    if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) {
        throw new Error(
            `registry-authority: generation ${at} has a bad index ${String(index)}`);
    }

    const fingerprint = row['fingerprint'];
    if (typeof fingerprint !== 'string' || !FINGERPRINT_PATTERN.test(fingerprint)) {
        throw new Error(
            `registry-authority: generation ${index} has a malformed fingerprint`);
    }

    const createdAt = row['createdAt'];
    if (typeof createdAt !== 'string' || createdAt.length === 0) {
        throw new Error(`registry-authority: generation ${index} has no createdAt`);
    }

    const status = row['status'];
    if (typeof status !== 'string' || !STATUSES.includes(status as AuthorityGenerationStatus)) {
        throw new Error(
            `registry-authority: generation ${index} has an unknown status ${String(status)}`);
    }

    return { index, fingerprint, createdAt, status: status as AuthorityGenerationStatus };
}

/** The next free index: max + 1, retired generations included, 0 when empty. */
export function nextAuthorityIndex(state: RegistryAuthorityState): number {
    let next = 0;
    for (const generation of state.generations) {
        if (generation.index >= next) next = generation.index + 1;
    }
    return next;
}

/** The generation a new ceremony pins, or null when the account has none. */
export function activeGeneration(state: RegistryAuthorityState): AuthorityGeneration | null {
    return state.generations.find(g => g.status === 'active') ?? null;
}

/**
 * Append a generation as the new `active`, retiring whichever was.
 *
 * PURE: the input document is untouched, so a caller that loses a
 * compare-and-set re-reads and re-applies this against what actually landed
 * instead of writing back a stale list.
 */
export function appendGeneration(
    state: RegistryAuthorityState,
    generation: { index: number; fingerprint: string; createdAt: string },
): RegistryAuthorityState {
    requireIndex(generation.index);
    if (state.generations.some(g => g.index === generation.index)) {
        throw new Error(
            `registry-authority: generation index ${generation.index} already exists`);
    }
    if (!FINGERPRINT_PATTERN.test(generation.fingerprint)) {
        throw new Error('registry-authority: malformed fingerprint');
    }
    const retired: AuthorityGeneration[] = state.generations.map(g =>
        g.status === 'active' ? { ...g, status: 'retired' as const } : { ...g });
    return {
        v: REGISTRY_AUTHORITY_DOCUMENT_VERSION,
        generations: [...retired, {
            index: generation.index,
            fingerprint: generation.fingerprint,
            createdAt: generation.createdAt,
            status: 'active' as const,
        }].sort((a, b) => a.index - b.index),
    };
}

function requireIndex(index: number): void {
    if (!Number.isInteger(index) || index < 0) {
        throw new Error(`registry-authority: illegal generation index ${String(index)}`);
    }
}
